import React from 'react';
import { Link } from 'react-router-dom';

/**
 * DI Research Agent — /agents/research-agent
 *
 * Positioning: a workflow layer that orchestrates existing DI capabilities
 * for a research objective. NOT a claim of autonomous science or a
 * standalone model suite.
 *
 * Provenance discipline applied to this copy:
 * - Multimodal foundation (clinical / imaging / pathology / acoustic) is
 *   described as built, matching the public FAQ's existing description.
 * - Knowledge/ontology infrastructure is described as "knowledge
 *   infrastructure" rather than a browsable/queryable tool, since there is
 *   no public graph-explorer UI.
 * - No internal architecture specifics (encoder names, embedding
 *   dimensions, database technology) are surfaced on this public page —
 *   those stay in Technology/Platform where appropriate, at whatever
 *   detail level Ram decides for those pages.
 * - Autonomous-scientist / clinical-decision-maker framing is explicitly
 *   ruled out in the boundaries section, not just omitted.
 *
 * Before publishing, re-check every capability line below against what is
 * actually live in Platform/Technology/Solutions at merge time — this
 * draft is based on prior architecture notes, not a fresh audit of the
 * current pages.
 */

const workflowStages = [
  {
    stage: 'Research question',
    detail: 'A researcher frames a question about disease characteristics, mechanisms, biomarkers, or patient patterns.',
  },
  {
    stage: 'Knowledge',
    detail: 'Relevant literature and DI\u2019s disease-concept knowledge infrastructure are drawn on to ground the question.',
  },
  {
    stage: 'Data',
    detail: 'Available clinical, imaging, pathology, and acoustic evidence relevant to the question is assembled.',
  },
  {
    stage: 'Multimodal analysis',
    detail: 'DI\u2019s multimodal representation and alignment capabilities analyze the assembled evidence together.',
  },
  {
    stage: 'Discovery',
    detail: 'Candidate patterns \u2014 in biomarkers, mechanisms, or patient stratification \u2014 are surfaced as research outputs.',
  },
  {
    stage: 'Human interpretation',
    detail: 'The researcher reviews, interprets, and validates the findings. The agent does not draw final conclusions on its own.',
  },
];

const capabilityMap = [
  {
    stage: 'Research question',
    capability: 'Disease Intelligence',
    note: 'High-level disease characterization, described at a conceptual level.',
  },
  {
    stage: 'Knowledge',
    capability: 'Literature & knowledge infrastructure',
    note: 'Internal ontology-anchored infrastructure supporting the workflow \u2014 not a public, browsable knowledge graph.',
  },
  {
    stage: 'Data',
    capability: 'Clinical \u00b7 imaging \u00b7 pathology \u00b7 acoustic evidence',
    note: 'The multimodal evidence types DI\u2019s core capabilities are built around.',
  },
  {
    stage: 'Analysis',
    capability: 'Multimodal representation & alignment',
    note: 'DI\u2019s cross-modal analysis approach, described here without internal implementation detail.',
  },
  {
    stage: 'Discovery',
    capability: 'Biomarkers \u00b7 mechanisms \u00b7 stratification',
    note: 'Presented as research outputs for review, not as validated clinical findings.',
  },
  {
    stage: 'Interpretation',
    capability: 'Human review & validation',
    note: 'The required final step before any finding is treated as evidence.',
  },
];

const boundaries = [
  'AI-assisted, not an autonomous scientific authority',
  'Supports researchers \u2014 it does not replace them',
  'Draws on existing DI capabilities and available research resources',
  'Findings require human interpretation and validation before use',
  'Not a production clinical decision-maker',
];

export default function ResearchAgent() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper">
      {/* ---------------------------------------------------------- Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="text-[11px] font-extrabold tracking-[0.28em] uppercase text-slate-400 mb-3">
          Agents
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
          DI Research Agent
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
          An AI-assisted research and discovery workflow for multimodal disease investigation.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="#workflow"
            className="text-white text-sm font-bold bg-[#187F73] px-5 py-2.5 rounded-xl border border-[#187F73] transition-colors duration-200 hover:bg-[#15695F]"
          >
            Explore the workflow
          </a>
          <Link
            to="/company/contact"
            className="text-sm font-bold text-[#187F73] px-5 py-2.5 rounded-xl border border-[#187F73]/40 transition-colors duration-200 hover:bg-[#187F73]/5"
          >
            Request a research demo
          </Link>
        </div>
      </section>

      {/* ---------------------------------------------------- What it does */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[6px_6px_0_0_rgba(24,127,115,0.12)] p-8">
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-4">What it does</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl">
            DI Research Agent brings together literature, disease knowledge, and multimodal
            evidence into a guided research workflow. It orchestrates DI\u2019s existing analytical
            capabilities to help researchers investigate disease characteristics, mechanisms,
            biomarkers, and patient or cohort patterns \u2014 while keeping the researcher in
            control of interpretation and validation at every step.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------- Workflow */}
      <section id="workflow" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">Research workflow</h2>
        <p className="text-slate-500 mb-8 max-w-2xl">
          Question &rarr; Knowledge &rarr; Data &rarr; Analysis &rarr; Discovery &rarr; Interpretation
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {workflowStages.map((s, i) => (
            <div
              key={s.stage}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-[4px_4px_0_0_rgba(24,127,115,0.10)]"
            >
              <div className="text-[#187F73] text-xs font-bold tracking-wide mb-2">
                Step {i + 1}
              </div>
              <div className="font-semibold text-slate-900 mb-1.5">{s.stage}</div>
              <p className="text-sm text-slate-600 leading-relaxed">{s.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------- Capability map */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
          How the workflow maps to DI capabilities
        </h2>
        <p className="text-slate-500 mb-6 max-w-2xl">
          Every stage draws on a specific DI capability \u2014 shown at the level we can
          currently demonstrate, not the level of internal architecture.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500">
                <th className="px-5 py-3 font-semibold">Stage</th>
                <th className="px-5 py-3 font-semibold">DI capability</th>
                <th className="px-5 py-3 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {capabilityMap.map((row, i) => (
                <tr key={row.stage} className={i % 2 === 1 ? 'bg-slate-50/50' : ''}>
                  <td className="px-5 py-4 font-medium text-slate-900 align-top whitespace-nowrap">
                    {row.stage}
                  </td>
                  <td className="px-5 py-4 text-slate-700 align-top">{row.capability}</td>
                  <td className="px-5 py-4 text-slate-500 align-top">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ------------------------------------------------- Platform tie-in */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-[#187F73]/5 rounded-2xl border border-[#187F73]/15 p-8">
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-4">
            How this connects to the DI Platform
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mb-6">
            The DI Platform provides the underlying intelligence \u2014 its capabilities and
            technology. DI Research Agent orchestrates that foundation into a guided workflow
            a researcher can follow end to end.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[#187F73]">
            <span className="bg-white rounded-lg px-3 py-1.5 border border-[#187F73]/20">DI Platform</span>
            <span aria-hidden="true">&rarr;</span>
            <span className="bg-white rounded-lg px-3 py-1.5 border border-[#187F73]/20">Capabilities</span>
            <span aria-hidden="true">&rarr;</span>
            <span className="bg-white rounded-lg px-3 py-1.5 border border-[#187F73]/20">Research Agent</span>
            <span aria-hidden="true">&rarr;</span>
            <span className="bg-white rounded-lg px-3 py-1.5 border border-[#187F73]/20">Guided workflow</span>
            <span aria-hidden="true">&rarr;</span>
            <span className="bg-white rounded-lg px-3 py-1.5 border border-[#187F73]/20">Human interpretation</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/platform" className="text-[#187F73] font-semibold hover:underline">Platform &rarr;</Link>
            <Link to="/technology" className="text-[#187F73] font-semibold hover:underline">Technology &rarr;</Link>
            <Link to="/solutions/precision-medicine" className="text-[#187F73] font-semibold hover:underline">Precision Medicine &rarr;</Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ Boundaries */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-8">
          <h2 className="font-serif text-2xl font-bold text-slate-900 mb-4">
            What DI Research Agent is \u2014 and isn\u2019t
          </h2>
          <ul className="space-y-2.5">
            {boundaries.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#187F73] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-slate-500 leading-relaxed">
            Research outputs from DI Research Agent are not represented as clinically validated
            conclusions unless independently validated. The agent does not perform autonomous
            scientific discovery or make clinical decisions.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------------- CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-2xl bg-[#187F73] px-8 py-10 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-3">
            See DI Research Agent in context
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7">
            Talk to the team about how the Research Agent workflow fits your research objective.
          </p>
          <Link
            to="/company/contact"
            className="inline-block text-[#187F73] text-sm font-bold bg-white px-6 py-3 rounded-xl transition-colors duration-200 hover:bg-white/90"
          >
            Request a research demo
          </Link>
        </div>
      </section>
    </main>
  );
}
